import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { Form, Row, Col } from 'react-bootstrap'
import SelectWithOptionalTextField from '../../../components/SelectWithOptionalTextField'
import FormInput from '../../../components/FormInput'
import { userRegionIsEU } from '../../../functions/userPermissions'
import { showPatientConsentForm } from '../../../functions/dateHandling'
import { handleChartFieldChange, submitChartGeneralInfo } from '../reducers/chartFormReducer'


const ChartBasicInfo = (props) => {
  const chartShouldSaveOnBlur = props.patientChartUrl && props.trialSiteNumber !== '' && props.chartReviewerId !== '' && props.chartReviewerRole !== '' && props.patientIdentifier !== '' && props.site?.url !== ''

  const handleChange = (e) => {
    const { name, value } = e.target
    props.handleChartFieldChange(name, value)
  }

  const handleRoleChange = (e) => {
    const { name, value } = e.target
    props.handleChartFieldChange(name, value)
    if (name === 'chartReviewerRole' && value?.toLowerCase() !== 'other') {
      props.handleChartFieldChange('chartReviewerRoleOther', '')
    }
  }

  const handleDateChange = (name, date) => {
    props.handleChartFieldChange(name, date)
    if (chartShouldSaveOnBlur && ((date instanceof Date && !isNaN(date)) || date === null)) {
      const updatedChart = { ...props, [name]: date, }
      return props.submitChartGeneralInfo(updatedChart)
    }
  }

  const handleBlur = (e) => {
    if (chartShouldSaveOnBlur) {
      props.submitChartGeneralInfo(props)
    }
  }

  const handleSiteChange = (e) => {
    let selectedSite = props.currentUser.sites.find(userSite => userSite.url === e.target.value)

    if (!selectedSite) {
      selectedSite = {}
    }

    if (selectedSite && selectedSite.region.toLowerCase() === 'eu') {
      props.handleChartFieldChange('localRequirementsExemption', false)
    } else {
      props.handleChartFieldChange('localRequirementsExemption', true)
    }

    props.handleChartFieldChange('trialSiteNumber', selectedSite.number || '')
    props.handleChartFieldChange('trialSiteNumber', selectedSite.number || '')
    return props.handleChartFieldChange(e.target.name, selectedSite)
  }

  const {
    trialSiteNumber,
    chartReviewerId,
    chartReviewerRole,
    chartReviewerRoleOther,
    patientIdentifier,
    chartForReview,
    currentUser,
    metInclusionCriteria,
    metExclusionCriteria,
    informedConsentDate,
    localRequirementsExemption,
    site,
    showChartGeneralValidation,
  } = props

  const chartReviewerRoleOptions = [
    {value: 'Study Coordinator',
    fieldName: 'studyCoordinator'},
    {value: 'Investigator',
    fieldName: 'investigator'},
    {value: 'Co-Investigator or Fellow',
    fieldName: 'coInvestigatorOrFellow'},
    {value: 'Other',
    fieldName: 'other',
    textFields: [{
      displayName: 'Please Specify',
      fieldName: 'chartReviewerRoleOther'
    }]},
  ]

  const setChartReviewRole = () => {
    let reviewerRole
    if (chartForReview) {
      reviewerRole = chartReviewerRole || ''
    } else {
      reviewerRole = chartReviewerRole || currentUser.last_role_used || ''
    }
    return props.handleChartFieldChange('chartReviewerRole', reviewerRole)
  }

  const setChartReviewRoleOther = () => {
    let reviewerRoleOther
    if (chartForReview) {
      reviewerRoleOther = chartReviewerRoleOther || ''
    } else {
      reviewerRoleOther = chartReviewerRoleOther || currentUser.last_role_used_other || ''
    }
    return props.handleChartFieldChange('chartReviewerRoleOther', reviewerRoleOther)
  }

  const setChartSite = () => {
    if (!chartForReview && currentUser.sites.length === 1) {
      const selectedSite = currentUser.sites[0]
      if (!props.patientChartUrl) {
        if (selectedSite && selectedSite.region?.toLowerCase() === 'eu') {
          props.handleChartFieldChange('localRequirementsExemption', false)
        } else {
          props.handleChartFieldChange('localRequirementsExemption', true)
        }
      }
      if (selectedSite && selectedSite.number) {
        props.handleChartFieldChange('trialSiteNumber', selectedSite.number)
      }
      return props.handleChartFieldChange('site', selectedSite)
    }
  }


  const setChartReviewerId = () => {
    let reviewerId
    if (chartForReview) {
      reviewerId = chartReviewerId || ''
    } else {
      reviewerId = chartReviewerId || currentUser.number || ''
    }

    return props.handleChartFieldChange('chartReviewerId', reviewerId)
  }


  const setTrialSiteNumber = () => {
    let siteNumber
    if (!chartForReview && currentUser && currentUser.site) {
      siteNumber = trialSiteNumber || currentUser.site.number || ''
    } else {
      siteNumber = trialSiteNumber || ''
    }

    return props.handleChartFieldChange('trialSiteNumber', siteNumber)
  }


  const setDefaultValues = () => {
    setChartReviewRole()
    setChartReviewRoleOther()
    setChartReviewerId()
    setTrialSiteNumber()
    setChartSite()
  }

  useEffect(setDefaultValues, [])


  const compareSites = (a, b) => {
    if (`${a.region} - ${a.name}` > `${b.region} - ${b.name}`) {
      return 1
    }

    if (`${a.region} - ${a.name}` < `${b.region} - ${b.name}`) {
      return -1
    }

    return 0
  }

  return (
    <div className="ChartBasicInfo animate__fadeOut">
      <Form validated={props.chartIsIncompleteModalData.length !== 0 || showChartGeneralValidation}>

        {Array.isArray(currentUser.sites) && currentUser.sites.length > 1 ?
          <div>
            <Row style={{ paddingBottom: "0" }}>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Form.Label>
                  Trial Site
                </Form.Label>
              </Col>
            </Row>

            <Row style={{ paddingTop: "0", marginBottom: "1rem" }}>
              <Col xs={12} sm={12} md={6} lg={6}>
                <Form.Control
                  as="select"
                  name='site'
                  value={site.url}
                  onChange={handleSiteChange}
                  onBlur={handleBlur}
                  disabled={chartForReview || site.url}
                  required={true}
                >
                  <option value="">Select a trial site</option>
                  {currentUser.sites.sort(compareSites).map(option => {
                    return (
                      <option
                        key={option.url}
                        value={option.url}
                      >
                        {option.region} - {option.name}
                      </option>
                    )
                  })}
                </Form.Control>
              </Col>
            </Row>
          </div>
          :
          null
        }

        {site && (site.url || chartForReview) ?
          <>
            <FormInput
              label={'Trial Site Number'}
              name={'trialSiteNumber'}
              value={trialSiteNumber}
              type={'number'}
              placeholder={'Enter a Trial Site Number'}
              xs={12}
              sm={12}
              md={8}
              lg={6}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={chartForReview || trialSiteNumber !== ''}
              required={true}
            />

            <FormInput
              label={'Chart Reviewer ID'}
              name={'chartReviewerId'}
              value={chartReviewerId}
              type={'number'}
              placeholder={'Enter your Chart Reviewer ID'}
              xs={12}
              sm={12}
              md={8}
              lg={6}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={chartForReview || chartReviewerId !== ''}
              required={true}
            />

            <SelectWithOptionalTextField
              label={'Role of the person completing this questionnaire'}
              options={chartReviewerRoleOptions}
              handleChange={handleRoleChange}
              selectFieldName={'chartReviewerRole'}
              selected={chartReviewerRole}
              onBlur={handleBlur}
              disabled={chartForReview}
              placeholder="Please make a selection"
              required={true}
              {...props}
            />

            <FormInput
              label={'Patient Number'}
              name={'patientIdentifier'}
              value={patientIdentifier}
              type={'number'}
              placeholder={'Enter a Patient Number'}
              xs={12}
              sm={12}
              md={8}
              lg={6}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={chartForReview}
              required={true}
            />

            {showPatientConsentForm(props.chartCreatedDate) ?
              <>
                {userRegionIsEU(currentUser) && site?.country ?
                  <FormInput
                    label={site.country.toLowerCase() === 'france' ? 'Date of end of opt-out period' : 'Date of informed consent'}
                    name="informedConsentDate"
                    value={informedConsentDate}
                    type="date"
                    placeholder="Enter date"
                    xs={12}
                    sm={12}
                    md={8}
                    lg={6}
                    onChange={date => handleDateChange('informedConsentDate', date)}
                    onBlur={handleBlur}
                    disabled={chartForReview || localRequirementsExemption}
                    required={!localRequirementsExemption}
                  />
                  :
                  null
                }

                {userRegionIsEU(currentUser) ?
                  <>
                    <Form.Check
                      id="localRequirementsExemption"
                      name="localRequirementsExemption"
                      label='According to local requirements, if informed consent is not required please tick box'
                      checked={localRequirementsExemption}
                      onBlur={handleBlur}
                      onChange={() => props.handleChartFieldChange('localRequirementsExemption', !localRequirementsExemption)}
                      disabled={chartForReview}
                    />
                    <br />
                  </>
                  :
                  null
                }

                <p>
                  Does the patient fulfill the following <strong>inclusion criteria</strong>:
                </p>

                <ol>
                  <li>
                    All patients with a documented GNBI (before or after starting cefiderocol) who for this infection received continuous cefiderocol treatment for at least 72 hours as routine practice.
                  </li>
                  <li>
                    Patients whose medical records include the following essential data elements:
                  </li>
                  <ul>
                    <li>
                      Start and stop dates of cefiderocol and dosing regimen (dose, duration, frequency)
                    </li>
                    <li>
                      Documentation of GNBI (infection site, pathogen).
                    </li>
                    <li>
                      The clinical outcome of the indicated GNBI at the end of the cefiderocol treatment
                    </li>
                    <li>
                      The outcome of the hospitalization: in-hospital mortality or discharge status
                    </li>
                  </ul>


                  <li>
                    Documented evidence of informed consent by the patient (or a legally acceptable representative) where required by local or country regulations.
                  </li>
                </ol>

                <Form.Check
                  id="metInclusionCriteriaYes"
                  name="metInclusionCriteriaYes"
                  label="Yes (may proceed with data entry)"
                  type="radio"
                  checked={metInclusionCriteria === true}
                  onBlur={handleBlur}
                  onChange={() => props.handleChartFieldChange('metInclusionCriteria', true)}
                  disabled={chartForReview}
                  required={metInclusionCriteria !== true && metInclusionCriteria !== false}
                />

                <Form.Check
                  id="metInclusionCriteriaNo"
                  name="metInclusionCriteriaNo"
                  label="No (stop data entry)"
                  type="radio"
                  checked={metInclusionCriteria === false}
                  onBlur={handleBlur}
                  onChange={() => props.handleChartFieldChange('metInclusionCriteria', false)}
                  disabled={chartForReview}
                  required={metInclusionCriteria !== true && metInclusionCriteria !== false}
                />

                <br />

                <p>
                  Does the patient fulfill the following <strong>exclusion criteria</strong>:
                </p>

                <ol>
                  <li>
                    Patients with incomplete medical records for essential data elements on cefiderocol use, infection, or outcome assessment
                  </li>
                  <li>
                    Patients receiving cefiderocol through a clinical trial, compassionate use, or early access program, or other means before the cefiderocol is commercially available {userRegionIsEU(currentUser) ? 'in the respective country' : null}
                  </li>
                  <li>
                    Patients with any prior treatment of cefiderocol before the current hospitalization
                  </li>

                  {userRegionIsEU(currentUser) ?
                    <li>
                      {'Patient < 18 years old'}
                    </li>
                    :
                    null
                  }
                </ol>

                <Form.Check
                  id="metExclusionCriteriaYes"
                  name="metExclusionCriteriaYes"
                  label="Yes (stop data entry)"
                  type="radio"
                  checked={metExclusionCriteria === true}
                  onBlur={handleBlur}
                  onChange={() => props.handleChartFieldChange('metExclusionCriteria', true)}
                  disabled={chartForReview}
                  required={metExclusionCriteria !== true && metExclusionCriteria !== false}
                />

                <Form.Check
                  id="metExclusionCriteriaNo"
                  name="metExclusionCriteriaNo"
                  label="No (may proceed with data entry)"
                  type="radio"
                  checked={metExclusionCriteria === false}
                  onBlur={handleBlur}
                  onChange={() => props.handleChartFieldChange('metExclusionCriteria', false)}
                  disabled={chartForReview}
                  required={metExclusionCriteria !== true && metExclusionCriteria !== false}
                />
              </>
              :
              null
            }
          </>
          :
          null
        }
      </Form>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    showChartGeneralValidation: state.chartFormReducer.showChartGeneralValidation,
    chartIsIncompleteModalData: state.chartFormReducer.chartIsIncompleteModalData,
    trialSiteNumber: state.chartFormReducer.trialSiteNumber,
    chartReviewerId: state.chartFormReducer.chartReviewerId,
    chartReviewerRole: state.chartFormReducer.chartReviewerRole,
    chartReviewerRoleOther: state.chartFormReducer.chartReviewerRoleOther,
    patientIdentifier: state.chartFormReducer.patientIdentifier,
    patientChartUrl: state.chartFormReducer.patientChartUrl,
    site: state.chartFormReducer.site,
    metInclusionCriteria: state.chartFormReducer.metInclusionCriteria,
    metExclusionCriteria: state.chartFormReducer.metExclusionCriteria,
    informedConsentDate: state.chartFormReducer.informedConsentDate,
    localRequirementsExemption: state.chartFormReducer.localRequirementsExemption,
    chartCreatedDate: state.chartFormReducer.chartCreatedDate,
    
    currentUser: state.loginReducer.currentUser,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    handleChartFieldChange: (key, value) => dispatch(handleChartFieldChange(key, value)),
    submitChartGeneralInfo: (props) => dispatch(submitChartGeneralInfo(props))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChartBasicInfo)
